/**
 * Root Application Shell & Router Component
 * Configures top-level client routes, persistent shell layouts (Navbar, Footer),
 * and global user interaction overlays (Consent Banner, Onboarding Tutorial).
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Layout Component Shells
import Navbar from './Components/Layout/Navbar';
import Footer from './Components/Layout/Footer';
import ConsentBanner from './Components/Layout/ConsentBanner';
import TutorialOverlay from './Components/Layout/TutorialOverlay';

// Public & General Information Pages
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Community from './pages/Community';

// Authentication & User Account Pages
import Login from './pages/Login/Login';
import ResetPassword from './pages/Login/ResetPassword';
import AuthConfirm from './pages/AuthConfirm';
import Account from './pages/Login/Account';

// Trust Section Pages
import CommunityTrust from './pages/TrustPage';
import TrustCategoryPage from './pages/Trust/TrustCategoryPage';
import Trustees from './pages/Trust/Trustees';
import History from './pages/Trust/History';

// Social Club Section Pages
import Social from './pages/social/Social';
import SocialBooking from './pages/social/SocialBooking';

// Administration Portal Pages
import AdminEvents from './pages/Admin/AdminEvents';
import AdminAccount from './pages/Admin/AdminAccount';
import ContactDetailsManagement from './pages/Admin/ContactDetailsManagement';
import SiteContentManagement from './pages/Admin/SiteContentManagement';
import TrusteeManagement from './pages/Admin/TrusteeManagement';
import AdminSocialStaff from './pages/Admin/AdminSocialStaff';
import AdminCreateAccount from './pages/Admin/AdminCreateAccount';

/**
 * Main App Component rendering top navigation bar, main route container,
 * global overlays, and persistent footer.
 */
function App() {
  return (
    <div className="App">
      {/* Persistent Navigation Bar Header */}
      <Navbar />

      {/* Global User Consent Banner & Onboarding Tutorial Overlays */}
      <ConsentBanner />
      <TutorialOverlay />

      {/* Main Accessible Landmark Container */}
      <main id="main-content" className="App-content">
        <Routes>
          {/* Public & Information Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/community" element={<Community />} />

          {/* Authentication & User Account Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth-confirm" element={<AuthConfirm />} />
          <Route path="/account" element={<Account />} />

          {/* Community Trust Routes */}
          <Route path="/trust" element={<CommunityTrust />} />
          <Route path="/trust/:category" element={<TrustCategoryPage />} />
          <Route path="/trustees" element={<Trustees />} />
          <Route path="/history" element={<History />} />

          {/* Social Club Routes */}
          <Route path="/social" element={<Social />} />
          <Route path="/social/staff" element={<Navigate to="/trustees" replace />} />
          <Route path="/social/booking" element={<SocialBooking />} />
          <Route path="/social/contact" element={<Navigate to="/contact" replace />} />

          {/* Administration Routes */}
          <Route path="/admin/events" element={<AdminEvents />} />
          <Route path="/admin-account" element={<AdminAccount />} />
          <Route path="/admin/site-content" element={<SiteContentManagement />} />
          <Route path="/admin/contact-details" element={<ContactDetailsManagement />} />
          <Route path="/admin/trustees" element={<TrusteeManagement />} />
          <Route path="/admin/social-staff" element={<AdminSocialStaff />} />
          <Route path="/admin/create-account" element={<AdminCreateAccount />} />
        </Routes>
      </main>
      
      {/* Persistent Footer Shell */}
      <Footer />
    </div>
  );
}

export default App;